import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Award, Star, FileText, MessageSquare, CheckCircle,
  BookOpen, ScanLine, Calendar, Trophy, Zap, TrendingUp,
  Lock, Shield, FolderOpen, ArrowRightLeft, ThumbsUp, Reply, Crown,
  GraduationCap, Flame, MessageCircle, Sparkles, CreditCard,
  ClipboardCheck, Bell, User, ChevronDown, ChevronUp, Info
} from 'lucide-react';

import API from '../utils/api';
import { useSyncRefresh } from '../hooks/useSyncRefresh';

var ICON_MAP = {
  FileText: FileText, MessageSquare: MessageSquare, CheckCircle: CheckCircle,
  BookOpen: BookOpen, ScanLine: ScanLine, Calendar: Calendar,
  Star: Star, Award: Award, FolderOpen: FolderOpen,
  ArrowRightLeft: ArrowRightLeft, ThumbsUp: ThumbsUp, Reply: Reply,
  Crown: Crown, GraduationCap: GraduationCap, Flame: Flame,
  MessageCircle: MessageCircle, Sparkles: Sparkles, CreditCard: CreditCard,
  ClipboardCheck: ClipboardCheck, Zap: Zap, Shield: Shield,
  Trophy: Trophy, Bell: Bell, User: User, FileDown: FileText,
};

var STAT_CONFIG = [
  { key: 'journal_entries', label: 'Journal Entries', icon: FileText, color: '#5A4E9E', perPoint: 10 },
  { key: 'messages_analyzed', label: 'Messages Analyzed', icon: MessageSquare, color: '#2D2560', perPoint: 15 },
  { key: 'green_scores', label: 'Green Scores', icon: CheckCircle, color: '#10B981', perPoint: 30 },
  { key: 'blue_scores', label: 'Blue Scores', icon: Shield, color: '#3B82F6', perPoint: 20 },
  { key: 'calendar_events', label: 'Calendar Events', icon: Calendar, color: '#5A4E9E', perPoint: 5 },
  { key: 'plan_scans', label: 'Plan Scans', icon: ScanLine, color: '#EAB308', perPoint: 25 },
  { key: 'case_files', label: 'Case Files', icon: FolderOpen, color: '#F97316', perPoint: 10 },
  { key: 'change_requests_sent', label: 'Change Requests', icon: ArrowRightLeft, color: '#10B981', perPoint: 15 },
  { key: 'change_requests_approved', label: 'Requests Approved', icon: ThumbsUp, color: '#10B981', perPoint: 25 },
  { key: 'lessons_completed', label: 'Lessons Completed', icon: BookOpen, color: '#5A4E9E', perPoint: 20 },
  { key: 'quizzes_completed', label: 'Quizzes Done', icon: ClipboardCheck, color: '#2D2560', perPoint: 25 },
  { key: 'perfect_quizzes', label: 'Perfect Quizzes', icon: Zap, color: '#EAB308', perPoint: 50 },
  { key: 'courses_purchased', label: 'Courses Purchased', icon: CreditCard, color: '#F97316', perPoint: 0 },
  { key: 'courses_completed', label: 'Courses Completed', icon: GraduationCap, color: '#10B981', perPoint: 0 },
  { key: 'series_completed', label: 'Series Completed', icon: Trophy, color: '#EAB308', perPoint: 0 },
  { key: 'companion_interactions', label: 'Companion Chats', icon: Sparkles, color: '#2D2560', perPoint: 5 },
  { key: 'coparent_messages', label: 'Co-Parent Messages', icon: MessageCircle, color: '#5A4E9E', perPoint: 0 },
];

var LEVEL_TITLES = [
  'Newcomer', 'Explorer', 'Contributor', 'Advocate', 'Champion',
  'Veteran', 'Expert', 'Master', 'Legend', 'Paragon',
  'Sage', 'Grandmaster', 'Luminary', 'Transcendent', 'Immortal'
];

function Compliance() {
  var [data, setData] = useState(null);
  var [pointsGuide, setPointsGuide] = useState(null);
  var [loading, setLoading] = useState(true);
  var [expandedCats, setExpandedCats] = useState({});
  var [showGuide, setShowGuide] = useState(false);

  useEffect(function () {
    Promise.all([
      axios.get(API + '/compliance/progress', { withCredentials: true }).catch(function () { return { data: null }; }),
      axios.get(API + '/compliance/points-guide', { withCredentials: true }).catch(function () { return { data: null }; }),
    ]).then(function (res) {
      setData(res[0].data);
      setPointsGuide(res[1].data);
    }).finally(function () { setLoading(false); });
  }, []);

  var refreshCompliance = useCallback(function() {
    Promise.all([
      axios.get(API + '/compliance/progress', { withCredentials: true }).catch(function () { return { data: null }; }),
      axios.get(API + '/compliance/points-guide', { withCredentials: true }).catch(function () { return { data: null }; }),
    ]).then(function (res) { setData(res[0].data); setPointsGuide(res[1].data); });
  }, []);
  useSyncRefresh(refreshCompliance);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-24" data-testid="compliance-loading">
        <div className="w-8 h-8 border-2 border-[#2D2560] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto card-surface p-12 text-center" data-testid="compliance-error">
        <p className="text-white/60">Unable to load progress data.</p>
      </div>
    );
  }

  var level = data.level || 1;
  var levelTitle = LEVEL_TITLES[Math.min(level - 1, LEVEL_TITLES.length - 1)];
  var pointsInLevel = data.points % 200;
  var progressPct = (pointsInLevel / 200) * 100;

  var earnedSet = {};
  (data.earned_badges || []).forEach(function (b) { earnedSet[b.id] = true; });

  var categories = {};
  (data.all_badges || []).forEach(function (b) {
    if (!categories[b.category]) categories[b.category] = [];
    categories[b.category].push(b);
  });

  var catNames = Object.keys(categories);

  var toggleCat = function (cat) {
    setExpandedCats(function (prev) {
      var next = Object.assign({}, prev);
      next[cat] = !next[cat];
      return next;
    });
  };

  var breakdown = data.points_breakdown || {};

  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Hero: Level & Points */}
      <div className="card-surface p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2D2560]/10 via-transparent to-[#5A4E9E]/10 pointer-events-none"></div>
        <div className="relative flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full border-[3px] border-[#2D2560] flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(217,70,239,0.15), rgba(139,92,246,0.15))' }}>
              <div className="text-center">
                <p className="text-3xl font-extrabold text-[#2D2560]">{level}</p>
                <p className="text-xs text-white/50 uppercase tracking-widest">Level</p>
              </div>
            </div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#2D2560] rounded-full">
              <p className="text-xs font-bold text-white whitespace-nowrap">{levelTitle}</p>
            </div>
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2 justify-center sm:justify-start">
              <Trophy className="w-6 h-6 text-[#EAB308]" />
              Your Progress
            </h2>
            <p className="text-white/50 text-sm mb-4">{data.total_badge_count} badges across {catNames.length} categories</p>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div>
                <p className="text-3xl font-extrabold">{data.points.toLocaleString()}</p>
                <p className="text-xs text-white/40">Total Points</p>
              </div>
              <div className="w-full sm:flex-1 sm:max-w-sm">
                <div className="flex justify-between text-xs text-white/40 mb-1">
                  <span>Level {level}</span>
                  <span>Level {level + 1}</span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden border border-white/10">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: progressPct + '%', background: 'linear-gradient(90deg, #2D2560, #5A4E9E)' }}></div>
                </div>
                <p className="text-xs text-white/30 mt-1">{pointsInLevel}/200 points to next level</p>
              </div>
              <div className="flex gap-4">
                <div className="text-center">
                  <p className="text-lg font-bold text-[#EAB308]">{data.earned_badge_count}</p>
                  <p className="text-xs text-white/40">Badges</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-[#5A4E9E]">{data.weekly_activity}</p>
                  <p className="text-xs text-white/40">This Week</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Points Breakdown */}
      {(breakdown.course_completions > 0 || breakdown.series_completions > 0 || breakdown.category_completions > 0) && (
        <div className="card-surface p-5">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#EAB308]" />
            Points Breakdown
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg border border-white/5 bg-white/[0.02] text-center">
              <p className="text-lg font-bold">{(breakdown.base_activity || 0).toLocaleString()}</p>
              <p className="text-xs text-white/40">Activity</p>
            </div>
            <div className="p-3 rounded-lg border border-[#10B981]/20 bg-[#10B981]/5 text-center">
              <p className="text-lg font-bold text-[#10B981]">+{(breakdown.course_completions || 0).toLocaleString()}</p>
              <p className="text-xs text-white/40">Courses</p>
            </div>
            <div className="p-3 rounded-lg border border-[#EAB308]/20 bg-[#EAB308]/5 text-center">
              <p className="text-lg font-bold text-[#EAB308]">+{(breakdown.series_completions || 0).toLocaleString()}</p>
              <p className="text-xs text-white/40">Series</p>
            </div>
            <div className="p-3 rounded-lg border border-[#2D2560]/20 bg-[#2D2560]/5 text-center">
              <p className="text-lg font-bold text-[#2D2560]">+{(breakdown.category_completions || 0).toLocaleString()}</p>
              <p className="text-xs text-white/40">Category Bonuses</p>
            </div>
          </div>
        </div>
      )}

      {/* Badges — Collapsible Categories */}
      <div className="card-surface p-6" data-testid="badges-section">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-[#EAB308]" />
          Badges ({data.earned_badge_count}/{data.total_badge_count})
        </h3>
        <div className="space-y-2">
          {catNames.map(function (cat) {
            var catBadges = categories[cat];
            var catEarned = catBadges.filter(function (b) { return earnedSet[b.id]; }).length;
            var isComplete = catEarned === catBadges.length && catBadges.length > 0;
            var isExpanded = expandedCats[cat];
            var pct = catBadges.length > 0 ? Math.round((catEarned / catBadges.length) * 100) : 0;

            return (
              <div key={cat} className={'rounded-xl border transition-all ' + (isComplete ? 'border-[#10B981]/30 bg-[#10B981]/[0.03]' : 'border-white/[0.06] bg-white/[0.01]')}>
                <button onClick={function () { toggleCat(cat); }}
                  className="w-full flex items-center justify-between p-3 sm:p-4 text-left"
                  data-testid={'badge-cat-' + cat.replace(/\s+/g, '-').toLowerCase()}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {isComplete && <Trophy className="w-4 h-4 text-[#10B981] flex-shrink-0" />}
                    <span className="text-sm font-medium truncate">{cat}</span>
                    <span className="text-xs text-white/30 flex-shrink-0">{catEarned}/{catBadges.length}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden hidden sm:block">
                      <div className="h-full rounded-full" style={{ width: pct + '%', background: isComplete ? '#10B981' : 'linear-gradient(90deg, #2D2560, #5A4E9E)' }}></div>
                    </div>
                    <span className="text-xs text-white/40 w-8 text-right">{pct}%</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                    {isComplete && (
                      <div className="mb-3 p-2 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-[#10B981]" />
                        <span className="text-xs text-[#10B981] font-medium">Category Complete! +500 bonus points</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      {catBadges.map(function (badge) {
                        var earned = earnedSet[badge.id];
                        var IconComp = ICON_MAP[badge.icon] || Award;
                        return (
                          <div key={badge.id}
                            className={'relative p-3 rounded-xl border text-center transition-all ' +
                              (earned ? 'border-[#EAB308]/40 bg-[#EAB308]/5' : 'border-white/5 bg-white/[0.02] opacity-50')}
                            data-testid={'badge-' + badge.id}>
                            <div className={'w-9 h-9 rounded-full mx-auto mb-1.5 flex items-center justify-center ' +
                              (earned ? 'bg-[#EAB308]/20' : 'bg-white/5')}>
                              {earned ? <IconComp className="w-4 h-4 text-[#EAB308]" /> : <Lock className="w-3.5 h-3.5 text-white/20" />}
                            </div>
                            <p className={'text-xs font-semibold mb-0.5 leading-tight ' + (earned ? 'text-white' : 'text-white/40')}>{badge.name}</p>
                            <p className="text-xs text-white/30 leading-tight">{badge.desc}</p>
                            {earned && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#EAB308] rounded-full flex items-center justify-center">
                                <CheckCircle className="w-2.5 h-2.5 text-black" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity Stats */}
      <div className="card-surface p-6" data-testid="activity-stats">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#5A4E9E]" />
          Activity Breakdown
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {STAT_CONFIG.map(function (s) {
            var val = (data.stats && data.stats[s.key]) || 0;
            var pts = s.perPoint > 0 ? val * s.perPoint : null;
            return (
              <div key={s.key} className="flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]"
                data-testid={'stat-' + s.key}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: s.color + '15' }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{val.toLocaleString()}</p>
                  <p className="text-xs text-white/40">{s.label}</p>
                </div>
                {pts !== null && pts > 0 && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold" style={{ color: s.color }}>+{pts.toLocaleString()}</p>
                    <p className="text-xs text-white/30">pts</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* How to Earn Points — Expandable */}
      <div className="card-surface p-5" data-testid="points-guide">
        <button onClick={function () { setShowGuide(!showGuide); }}
          className="w-full flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2 text-white/60">
            <Info className="w-4 h-4 text-[#EAB308]" />
            How to Earn Points
          </h3>
          {showGuide ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </button>
        {showGuide && pointsGuide && (
          <div className="mt-4 space-y-2">
            {pointsGuide.activities.map(function (a) {
              var isHighValue = a.points >= 500;
              var isCourse = a.activity.toLowerCase().includes('course') || a.activity.toLowerCase().includes('series');
              return (
                <div key={a.activity}
                  className={'flex items-center gap-3 p-3 rounded-lg border transition-all ' +
                    (isHighValue ? 'border-[#EAB308]/30 bg-[#EAB308]/[0.03]' : isCourse ? 'border-[#10B981]/20 bg-[#10B981]/[0.02]' : 'border-white/5 bg-white/[0.02]')}>
                  <div className="flex-1 min-w-0">
                    <p className={'text-xs font-medium ' + (isHighValue ? 'text-[#EAB308]' : 'text-white')}>{a.activity}</p>
                    <p className="text-xs text-white/30">{a.description}</p>
                  </div>
                  <span className={'text-sm font-bold flex-shrink-0 ' + (isHighValue ? 'text-[#EAB308]' : isCourse ? 'text-[#10B981]' : 'text-white/60')}>
                    +{a.points.toLocaleString()}
                  </span>
                </div>
              );
            })}
            <div className="mt-3 p-3 rounded-lg bg-[#2D2560]/5 border border-[#2D2560]/20">
              <p className="text-xs text-[#2D2560] font-medium">Category Completion Bonus: +{pointsGuide.category_completion_bonus} pts per completed category ({pointsGuide.total_categories} categories available)</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Compliance;
